import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, DatePicker, Modal, Popconfirm, Space, Switch,
  Table, Tag, Tooltip, message,
} from 'antd';
import { DownloadOutlined, PrinterOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import { unreceivedApi, type UnreceivedItem, type UnreceivedOrder } from '../api/unreceived';
import UnreceivedPrintPreview, { type UnreceivedPrintItem } from '../components/unreceived/UnreceivedPrintPreview';
import DataMatrixImage from '../components/marking/DataMatrixImage';
import { rawToGs1 } from '../utils/dataMatrix';
import useAuthStore from '../store/authStore';

// Страница «Продажи без оприходования» (аварийный режим, сбой ЭДО) — unreceived_sales.md v3.
// Реестр позиций, сознательно не отправленных в 1С: печать A4 марок для ручной
// дофискализации, CSV, проверка появления марок в ШУТ 1С, закрытие строк.

const { RangePicker } = DatePicker;

interface Row extends UnreceivedItem {
  order_id: number;
  order_number: string | null;
  order_created_at: string | null;
  counterparty: string | null;
}

const money = (v: number) => `${Number(v).toLocaleString('ru-RU')} ₽`;

const qtyText = (r: Row) =>
  r.product_type === 'bulk'
    ? `${Number(r.quantity)} л`
    : `${Number(r.quantity)} ${r.unit || 'шт'}`;

const UnreceivedPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('marking:write');
  const canToggleMode = hasPermission('users:write');

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [orders, setOrders] = useState<UnreceivedOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [dmItem, setDmItem] = useState<Row | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await unreceivedApi.getReport({
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1].format('YYYY-MM-DD'),
        include_resolved: includeResolved,
      });
      setEnabled(data.enabled);
      setOrders(data.orders);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить отчёт');
    } finally {
      setLoading(false);
    }
  }, [range, includeResolved]);

  useEffect(() => {
    load();
  }, [load]);

  const rows: Row[] = useMemo(
    () =>
      orders.flatMap((o) =>
        o.items.map((it) => ({
          ...it,
          order_id: o.order_id,
          order_number: o.number,
          order_created_at: o.created_at,
          counterparty: o.counterparty,
        }))
      ),
    [orders]
  );

  const pendingSum = useMemo(
    () => orders.reduce((acc, o) => acc + Number(o.pending_sum), 0),
    [orders]
  );

  const printItems: UnreceivedPrintItem[] = useMemo(
    () =>
      rows
        .filter((r) => !r.resolved_at)
        .map((r) => ({ ...r, order_number: r.order_number })),
    [rows]
  );

  const handleCheck = async () => {
    setChecking(true);
    try {
      const { data } = await unreceivedApi.checkIn1C();
      const total = data.orders.reduce((n, o) => n + o.items.length, 0);
      const found = data.orders.reduce((n, o) => n + o.items.filter((i) => i.found).length, 0);
      message.success(`Проверено марок: ${total}, появились в 1С: ${found}`);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка проверки в 1С');
    } finally {
      setChecking(false);
    }
  };

  const handleResolve = async (r: Row) => {
    try {
      await unreceivedApi.resolveItem(r.id);
      message.success(`Позиция «${r.name}» закрыта`);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось закрыть позицию');
    }
  };

  const handleToggleMode = (checked: boolean) => {
    Modal.confirm({
      title: checked ? 'Включить аварийный режим?' : 'Выключить аварийный режим?',
      content: checked
        ? 'Позиции с марками, отсутствующими в ШУТ 1С, будут исключаться из чека ККМ/Реализации и попадать в этот реестр. Включать только при сбое ЭДО.'
        : 'Новые позиции снова будут уходить в 1С со штатным барьером «Продажа запрещена». Уже отложенные строки останутся в реестре до закрытия.',
      okText: checked ? 'Включить' : 'Выключить',
      okButtonProps: checked ? { danger: true } : {},
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await unreceivedApi.setSettings(checked);
          message.success(checked ? 'Аварийный режим включён' : 'Аварийный режим выключен');
          window.location.reload();
        } catch (e: any) {
          message.error(e?.response?.data?.detail || 'Не удалось переключить режим');
        }
      },
    });
  };

  const handleCsv = () => {
    const header = ['Код GS1', 'GTIN', 'Серийник', 'Товар', 'Тип', 'Кол-во/объём', 'Сумма', 'Заказ', 'Дата'];
    const esc = (v: string) => (v.includes(';') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v);
    const lines = rows.map((r) => {
      const gs1 = r.raw_code ? rawToGs1(r.raw_code) || r.raw_code : r.marking_code;
      return [
        gs1,
        r.gtin || '',
        r.serial_number || '',
        r.name,
        r.product_type === 'bulk' ? 'разлив' : 'штучный',
        qtyText(r),
        String(Number(r.sum).toFixed(2)).replace('.', ','),
        r.order_number || String(r.order_id),
        r.order_created_at ? dayjs(r.order_created_at).format('DD.MM.YYYY') : '',
      ].map(esc).join(';');
    });
    const csv = '\uFEFF' + [header.join(';'), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unreceived_marks_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<Row> = [
    {
      title: 'Заказ',
      key: 'order',
      width: 110,
      render: (_, r) => (
        <Link to={`/service-orders/${r.order_id}`}>№ {r.order_number || r.order_id}</Link>
      ),
    },
    {
      title: 'Дата',
      key: 'date',
      width: 100,
      render: (_, r) => (r.order_created_at ? dayjs(r.order_created_at).format('DD.MM.YYYY') : '—'),
    },
    {
      title: 'Клиент',
      key: 'counterparty',
      width: 160,
      render: (_, r) => r.counterparty || <span style={{ color: '#999' }}>розница</span>,
    },
    {
      title: 'Товар',
      key: 'name',
      render: (_, r) => (
        <span style={r.resolved_at ? { color: '#999', textDecoration: 'line-through' } : undefined}>
          {r.name}
        </span>
      ),
    },
    {
      title: 'Кол-во',
      key: 'qty',
      width: 90,
      render: (_, r) => qtyText(r),
    },
    {
      title: 'Сумма',
      key: 'sum',
      width: 110,
      render: (_, r) => money(Number(r.sum)),
    },
    {
      title: 'Марка',
      key: 'mark',
      width: 220,
      render: (_, r) => (
        <Space size={6}>
          <Tooltip title={r.has_full_code && r.raw_code ? 'Показать DataMatrix' : 'Нет криптохвоста — отсканируйте с бутылки'}>
            <Button
              size="small"
              icon={<QrcodeOutlined />}
              disabled={!r.has_full_code || !r.raw_code}
              onClick={() => setDmItem(r)}
            />
          </Tooltip>
          <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
            {r.marking_code}
          </span>
        </Space>
      ),
    },
    {
      title: 'В 1С',
      key: 'in_shut',
      width: 120,
      render: (_, r) =>
        r.in_shut === null || r.in_shut === undefined ? (
          <Tag>не проверялась</Tag>
        ) : r.in_shut ? (
          <Tag color="green">появилась</Tag>
        ) : (
          <Tag color="red">нет в ШУТ</Tag>
        ),
    },
    {
      title: 'Действие',
      key: 'action',
      width: 130,
      render: (_, r) =>
        r.resolved_at ? (
          <Tooltip title={r.resolved_note || undefined}>
            <Tag color="default">закрыта {dayjs(r.resolved_at).format('DD.MM')}</Tag>
          </Tooltip>
        ) : canWrite ? (
          <Popconfirm
            title="Закрыть позицию?"
            description="Подтверждаете, что товар дофискализован в 1С (продажа со сканом марки с распечатки)?"
            okText="Закрыть"
            cancelText="Отмена"
            onConfirm={() => handleResolve(r)}
          >
            <Button size="small" type="primary" ghost>
              Закрыть
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Card
      title="Продажи без оприходования"
      extra={
        <Space wrap>
          {canToggleMode && enabled !== null && (
            <Space size={8}>
              <span style={{ fontSize: 13 }}>Аварийный режим</span>
              <Switch checked={enabled} onChange={handleToggleMode} />
            </Space>
          )}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Обновить
          </Button>
        </Space>
      }
    >
      {enabled === false && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Аварийный режим выключен"
          description="Новые позиции с марками не из ШУТ 1С блокируются штатным барьером. Ниже — реестр ранее отложенных позиций."
        />
      )}
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={range}
          onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
          allowClear={false}
        />
        <Checkbox checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)}>
          Показать закрытые
        </Checkbox>
        <Button type="primary" onClick={handleCheck} loading={checking}>
          Проверить в 1С
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)} disabled={printItems.length === 0}>
          Печать A4
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleCsv} disabled={rows.length === 0}>
          CSV
        </Button>
      </Space>
      <div style={{ marginBottom: 8, fontWeight: 600 }}>
        Итого к дофискализации: {money(pendingSum)}
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: 'Нет позиций, ожидающих оприходования' }}
      />

      <Modal
        open={!!dmItem}
        onCancel={() => setDmItem(null)}
        footer={null}
        centered
        width={360}
      >
        {dmItem && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16 }}>
            <DataMatrixImage code={dmItem.raw_code || ''} scale={2} />
            <div style={{ marginTop: 12, fontWeight: 600, textAlign: 'center' }}>{dmItem.name}</div>
            <div style={{ marginTop: 4, wordBreak: 'break-all', fontSize: 12, userSelect: 'text' }}>
              {dmItem.marking_code}
            </div>
          </div>
        )}
      </Modal>

      <UnreceivedPrintPreview
        open={printOpen}
        items={printItems}
        onClose={() => setPrintOpen(false)}
      />
    </Card>
  );
};

export default UnreceivedPage;
